import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Check,
  GraduationCap,
  Briefcase,
  ArrowsClockwise,
  Users,
  Calendar,
  CalendarCheck,
  ChartLineUp,
  Trophy,
  Star,
  StarFour,
  Sparkle,
  Crown,
  Path,
  TrendUp,
  Rocket,
  CurrencyDollar,
  Code,
  UserCircle,
  IdentificationCard,
  Buildings,
  Storefront,
  Factory,
  PlayCircle,
  BookOpen,
  CheckCircle,
  Lightning,
  Clock,
  Timer,
  FireSimple,
  ThumbsUp,
  HandHeart,
  Target,
  ChartBar,
  CaretRight
} from 'phosphor-react';
import scalerBot from '../../assets/Agent.png';
import tracker from '../../utils/tracker';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  gap: 36px;

  @media (max-width: 768px) {
    gap: 0;
    max-width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const BotAvatar = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const BotImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ChatBubble = styled.div`
  background: #fefce8;
  border: 2px solid #fde047;
  border-radius: 0;
  padding: 16px 20px;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  max-width: fit-content;

  &::before {
    content: '';
    position: absolute;
    left: -14px;
    top: 20px;
    width: 0;
    height: 0;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    border-right: 14px solid #fde047;
  }

  &::after {
    content: '';
    position: absolute;
    left: -10px;
    top: 20px;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 12px solid #fefce8;
  }
`;

const ChatText = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
  animation: ${slideInFromLeft} 0.6s ease-out;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuestionNumberBubble = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: #64748b;
`;

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;

  @media (max-width: 768px) {
    gap: 48px;
  }
`;

const QuestionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ScenarioLabel = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  color: #78716c;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: -4px;
`;

const QuestionLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
  margin-bottom: 4px;
`;

const OptionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    max-width: 100%;
  }
`;

const OptionPill = styled.button`
  background: ${props => props.selected ? '#fff4ed' : '#FFFFFF'};
  color: ${props => props.selected ? '#D55D26' : props.disabled ? '#94a3b8' : '#1e293b'};
  border: 2px solid ${props => props.selected ? '#D55D26' : props.disabled ? '#e2e8f0' : '#e7e5e4'};
  border-radius: 0;
  padding: 12px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    border-color: ${props => props.disabled ? '#e2e8f0' : '#D55D26'};
    background: ${props => props.selected ? '#fff4ed' : props.disabled ? '#FFFFFF' : '#FFFFFF'};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#e2e8f0' : '#D55D26'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px rgba(213, 93, 38, 0.1)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const OptionIconWrapper = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 0;
  background: ${props => props.selected ? '#D55D26' : '#fef8f6'};
  color: ${props => props.selected ? 'white' : '#78716c'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
`;

const OptionContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const OptionText = styled.div`
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.4;
`;

const CheckIcon = styled.div`
  color: #D55D26;
  display: flex;
  align-items: center;
  opacity: ${props => props.selected ? 1 : 0};
  transition: opacity 0.2s ease;
  flex-shrink: 0;
`;

const ContinueButtonContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 200;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    bottom: 110px;
    right: 16px;
    left: auto;
  }
`;

const ContinueButton = styled.button`
  background: #D70666;
  color: white;
  border: none;
  border-radius: 0;
  padding: 14px 32px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(215, 6, 102, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #b8044d;
    box-shadow: 0 6px 16px rgba(215, 6, 102, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Icon mapping for options (moved outside component for consistency)
const getOptionIcon = (value) => {
  const icons = {
  // Screen 1: Profile (Non-Tech)
    'non-tech': <UserCircle size={20} weight="duotone" />,
    'it-services': <Briefcase size={20} weight="duotone" />,
    'technical': <Code size={20} weight="duotone" />,
    'fresh-graduate': <GraduationCap size={20} weight="duotone" />,

    // Screen 1: Profile (Tech)
    'student-freshgrad': <GraduationCap size={20} weight="duotone" />,
    'swe-product': <Buildings size={20} weight="duotone" />,
    'swe-service': <Briefcase size={20} weight="duotone" />,

    // Experience levels
    '0': <GraduationCap size={20} weight="duotone" />,
    '0-2': <Clock size={20} weight="duotone" />,
    '3-5': <Briefcase size={20} weight="duotone" />,
    '5+': <Trophy size={20} weight="duotone" />,

    // Skills (Non-Tech)
    'communication': <Users size={20} weight="duotone" />,
    'analytical': <ChartLineUp size={20} weight="duotone" />,
    'operations': <Target size={20} weight="duotone" />,
    'self-learning': <BookOpen size={20} weight="duotone" />,

    // Screen 2: Motivation
    'salary-growth': <CurrencyDollar size={20} weight="duotone" />,
    'interest': <Star size={20} weight="duotone" />,
    'job-stability': <CheckCircle size={20} weight="duotone" />,
    'peer-influence': <Users size={20} weight="duotone" />,

    // Target Roles (Non-Tech)
    'backend': <Code size={20} weight="duotone" />,
    'fullstack': <Crown size={20} weight="duotone" />,
    'data-ml': <ChartBar size={20} weight="duotone" />,
    'data-analyst': <ChartLineUp size={20} weight="duotone" />,
    'not-sure': <Target size={20} weight="duotone" />,

    // Target Roles (Tech)
    'faang-product': <Trophy size={20} weight="duotone" />,
    'backend-fullstack': <Code size={20} weight="duotone" />,
    'techlead-architect': <Crown size={20} weight="duotone" />,

    // Company types
    'product': <Buildings size={20} weight="duotone" />,
    'startup': <Rocket size={20} weight="duotone" />,
    'service': <Briefcase size={20} weight="duotone" />,
    'domain-specific': <Target size={20} weight="duotone" />,

    // Screen 3: Preparation (Non-Tech)
    'completed-course': <GraduationCap size={20} weight="duotone" />,
    'just-exploring': <Path size={20} weight="duotone" />,
    'havent-tried': <PlayCircle size={20} weight="duotone" />,
    'follow-tutorials': <BookOpen size={20} weight="duotone" />,
    'solve-problems': <Code size={20} weight="duotone" />,

    // Coding Practice (Tech)
    '0-10': <PlayCircle size={20} weight="duotone" />,
    '11-50': <Clock size={20} weight="duotone" />,
    '51-100': <FireSimple size={20} weight="duotone" />,
    '100+': <Trophy size={20} weight="duotone" />,

    // System Design (Tech)
    'led-multiple': <Crown size={20} weight="duotone" />,
    'participated': <Users size={20} weight="duotone" />,
    'learning': <BookOpen size={20} weight="duotone" />,

    // Portfolio (Tech)
    'active-5plus': <Trophy size={20} weight="duotone" />,
    'limited-1to5': <CheckCircle size={20} weight="duotone" />,
    'inactive': <Clock size={20} weight="duotone" />,
    'no-portfolio': <Target size={20} weight="duotone" />,

    // Interview Practice (Tech)
    'weekly': <Trophy size={20} weight="duotone" />,
    'monthly': <CalendarCheck size={20} weight="duotone" />,
    'rarely': <Clock size={20} weight="duotone" />,
    'never': <Target size={20} weight="duotone" />,

    // Time investment options
    '6-10': <Timer size={20} weight="duotone" />,
    '10+': <FireSimple size={20} weight="duotone" />
  };

  return icons[value] || null;
};

const MBAGroupedQuestionScreen = ({
  questions,
  responses,
  onResponse,
  initialChatText,
  chatResponseMap,
  questionStartIndex = 1,
  onAutoAdvance,
  onChatTextChange,
  hideChat = false
}) => {
  const [chatText, setChatText] = useState(initialChatText);

  const handleOptionSelect = (question, option, questionIndex) => {
    const questionId = question.id;
    const isMultiselect = question.isMultiselect || false;

    if (isMultiselect) {
      // Handle multiselect: toggle option in array
      const currentValue = Array.isArray(responses[questionId]) ? responses[questionId] : [];
      const isSelected = currentValue.includes(option.value);
      const maxSelections = question.maxSelections || 3;
      const minSelections = question.minSelections || 1;
      
      // Prevent selecting more than max
      if (!isSelected && currentValue.length >= maxSelections) {
        // User tried to select more than max - show feedback or prevent
        return;
      }
      
      // Prevent deselecting if we're at minimum
      if (isSelected && currentValue.length <= minSelections) {
        // User tried to deselect when at minimum - prevent
        return;
      }
      
      const newValue = isSelected
        ? currentValue.filter(v => v !== option.value)
        : [...currentValue, option.value];

      tracker.click({
        click_type: 'question_clicked',
        custom: {
          question_number: questionStartIndex + questionIndex,
          question_id: questionId,
          question_text: question.question,
          option_selected: option.value,
          option_label: option.label ?? option.value,
          is_multiselect: true,
          action: isSelected ? 'deselected' : 'selected'
        }
      });

      tracker.formInput({
        click_type: 'question_clicked',
        custom: {
          question_number: questionStartIndex + questionIndex,
          question_id: questionId,
          question_text: question.question,
          option_selected: option.value,
          option_label: option.label ?? option.value,
          is_multiselect: true,
          action: isSelected ? 'deselected' : 'selected'
        }
      });

      // Create a synthetic option object for multiselect
      const syntheticOption = { value: newValue, label: option.label };
      onResponse(questionId, syntheticOption, question);

      // Auto-advance if user has selected the maximum number of options
      if (newValue.length === maxSelections && onAutoAdvance) {
        // Check if all questions on this screen are answered
        const updatedResponses = { ...responses, [questionId]: newValue };
        const allAnswered = questions.every((q) => {
          if (q.optional) {
            return true;
          }
          if (q.conditional && q.showIf) {
            if (!q.showIf(updatedResponses)) return true;
          }
          const response = updatedResponses[q.id];
          // For multiselect questions, check min/max constraints
          if (q.isMultiselect) {
            if (!Array.isArray(response)) return false;
            const qMinSelections = q.minSelections || 1;
            const qMaxSelections = q.maxSelections || 3;
            return response.length >= qMinSelections && response.length <= qMaxSelections;
          }
          return response !== undefined && response !== null;
        });

        if (allAnswered) {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            onAutoAdvance();
          }, 1000);
        }
      }

      return;
    } else {
      // Handle single select: replace value
      // Don't do anything if this option is already selected
      if (responses[questionId] === option.value) {
        return;
      }

      tracker.click({
        click_type: 'question_clicked',
        custom: {
          question_number: questionStartIndex + questionIndex,
          question_id: questionId,
          question_text: question.question,
          option_selected: option.value,
          option_label: option.label ?? option.value
        }
      });

      tracker.formInput({
        click_type: 'question_clicked',
        custom: {
          question_number: questionStartIndex + questionIndex,
          question_id: questionId,
          question_text: question.question,
          option_selected: option.value,
          option_label: option.label ?? option.value
        }
      });

      onResponse(questionId, option, question);

      // Check if all questions on this screen will be answered after this selection
      const updatedResponses = { ...responses, [questionId]: option.value };
      const allAnswered = questions.every((q) => {
        if (q.optional) {
          return true;
        }
        return updatedResponses[q.id] !== undefined && updatedResponses[q.id] !== null;
      });
      const isLastQuestion = questionIndex === questions.length - 1;
      const isSingleQuestion = questions.length === 1;

      if (!isSingleQuestion && !isLastQuestion) {
        if (chatResponseMap && chatResponseMap[questionId] && chatResponseMap[questionId][option.value]) {
          const newChatText = chatResponseMap[questionId][option.value];
          setChatText(newChatText);

          if (onChatTextChange) {
            onChatTextChange(newChatText);
          }
        }
      }

      // Auto-scroll to next question (if not last question on this screen)
      if (!isLastQuestion) {
        // Scroll to next question after a brief delay
        setTimeout(() => {
          const nextQuestionElement = document.querySelector(`[data-question-index="${questionIndex + 1}"]`);
          if (nextQuestionElement) {
            nextQuestionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 200);
      }

      // Auto-advance to next page if all questions are answered
      if (allAnswered && onAutoAdvance) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          onAutoAdvance();
        }, 1000);
      }
    }
  };

  // Check if there's a multiselect question and if it's valid
  const hasMultiselectQuestion = questions.some(q => q.isMultiselect);
  const multiselectQuestion = questions.find(q => q.isMultiselect);
  
  let showContinueButton = false;
  if (hasMultiselectQuestion && multiselectQuestion) {
    const response = responses[multiselectQuestion.id];
    if (Array.isArray(response)) {
      const minSelections = multiselectQuestion.minSelections || 1;
      const maxSelections = multiselectQuestion.maxSelections || 3;
      showContinueButton = response.length >= minSelections && response.length <= maxSelections;
    }
  }

  const handleContinue = () => {
    if (onAutoAdvance && showContinueButton) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onAutoAdvance();
    }
  };

  return (
    <Container>
      {!hideChat && (
        <Header>
          <BotAvatar>
            <BotImage src={scalerBot} alt="Scaler Bot" />
          </BotAvatar>
          <ChatBubble>
            <ChatText key={chatText}>{chatText}</ChatText>
          </ChatBubble>
        </Header>
      )}

      <QuestionsContainer>
        {questions.map((question, questionIndex) => (
          <QuestionGroup key={question.id} data-question-index={questionIndex}>
            {question.isScenario && <ScenarioLabel>Scenario-Based Question</ScenarioLabel>}
            <QuestionLabel>{question.question}</QuestionLabel>
            {question.isMultiselect && (
              <div style={{ 
                fontSize: '0.875rem', 
                color: '#64748b', 
                marginBottom: '8px',
                fontWeight: 400
              }}>
                {Array.isArray(responses[question.id]) && responses[question.id].length > 0
                  ? `${responses[question.id].length} of ${question.maxSelections || 3} selected`
                  : `Select up to ${question.maxSelections || 3} goals`}
              </div>
            )}
            <OptionsRow>
              {question.options.map((option) => {
                const isMultiselect = question.isMultiselect || false;
                const isSelected = isMultiselect
                  ? Array.isArray(responses[question.id]) && responses[question.id].includes(option.value)
                  : responses[question.id] === option.value;
                
                // For multiselect, check if option should be disabled
                const currentSelections = isMultiselect 
                  ? (Array.isArray(responses[question.id]) ? responses[question.id].length : 0)
                  : 0;
                const maxSelections = question.maxSelections || 3;
                const minSelections = question.minSelections || 1;
                const isDisabled = isMultiselect && !isSelected && currentSelections >= maxSelections;
                
                return (
                  <OptionPill
                    key={option.value}
                    selected={isSelected}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleOptionSelect(question, option, questionIndex)}
                  >
                    <OptionIconWrapper selected={isSelected}>
                      {option.icon || getOptionIcon(option.value)}
                    </OptionIconWrapper>
                    <OptionContent>
                      <OptionText>{option.label}</OptionText>
                      <CheckIcon selected={isSelected}>
                        <Check size={20} weight="bold" />
                      </CheckIcon>
                    </OptionContent>
                  </OptionPill>
                );
              })}
            </OptionsRow>
          </QuestionGroup>
        ))}
      </QuestionsContainer>

      {showContinueButton && (
        <ContinueButtonContainer>
          <ContinueButton onClick={handleContinue}>
            Continue
            <CaretRight size={20} weight="bold" />
          </ContinueButton>
        </ContinueButtonContainer>
      )}
    </Container>
  );
};

export default MBAGroupedQuestionScreen;

